import React, { useEffect, useMemo, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom';
import FatalFailureScreen from './Components/FatalFailureScreen';
import isWaitingOnAsyncChecks from './functions/isWaitingOnAsyncChecks';
import HardwareChecks from './HardwareChecks/HardwareChecks';
import NetworkPingChecks from './NetworkPingChecks/NetworkPingCheck';
import NetworkSpeedCheck from './NetworkSpeedChecks/NetworkSpeedCheck';
import IntroVideo from './IntroVideo/IntroVideo';
import StudentASRChecks from './StudentASRChecks/StudentASRChecks';
import { PRE_CHECK_MACHINE_STATES, READY_MODULE_TEST_MODES } from './constants'
import { endStudentPreChecks, preCheckItemErroredAndMoveOn, resetStudentPreChecks, selectStudentPreChecks, startStudentPreChecks, updateSettings } from './studentPreChecksSlice';
import StudentVolumeChecks from './StudentVolumeChecks/StudentVolumeChecks';
import StudentPreCheckErrorBoundary from './Components/StudentPreCheckErrorBoundary'
import { getUserData } from '../../services/API'
import './StudentPreChecks.scss'
import VirtualScreen from '../Reader/VirtualScreen/VirtualScreen';
import CheckWrapper from './Components/CheckWrapper'
import { LOCAL_STORAGE_FLAGS } from './constants';
import StudentInformationService from '../../services/StudentInformationService';
import useStudentPreChecks from './useStudentPreChecks';
import shouldSkipReadyModule from './functions/shouldSkipReadyModule'
import useDocumentTitle from "A11y/hooks/useDocumentTitle";
import { GetBrandedAppName } from '../../services/BrandingService';
import { logSessionInfo } from '../../services/logger';
import { FEATURE_FLAGS } from '../../constants/featureFlags';

function StudentPreChecks(props){
    const {history, initialUserInteraction, readerManagerRef, speechProps, onPreCheckDone, readyModuleTestMode: testMode, forcedAssessment, failureReturnUrl, returnUrl, teacherMicTest, resetTeacherMicTestMode, fromStudentInbox, isNonVerbalAssignment, onLogEventReady, midSessionMicTroubleshoot, audioIssueType, midSessionTroubleshootSeen, onRestartSession, onRestartSessionSuppressingTroubleshoot} = props

    const dispatch = useDispatch()

    // Track whether component is mounted to prevent operations after unmount
    const isMountedRef = useRef(true);

    const [micIcon, setMicIcon] = useState(false);

    const studentPreChecks = useSelector(selectStudentPreChecks);
    const studentPreCheck = useStudentPreChecks({ itemToCheck: studentPreChecks.currentCheck });
    const userData = useMemo(() => getUserData(), []) //QUESTION: is this reliable that it the data is loaded at this point?

    const readyModuleTestMode = useMemo(() => {
        if(teacherMicTest){
            return READY_MODULE_TEST_MODES.RETURNED_USER_WITH_ASSESSMENTS
        }
        return testMode
    }, [teacherMicTest, testMode])

    useDocumentTitle(`Student Pre-Checks - ${GetBrandedAppName()}`);

    // Set the log event callback for the student pre-checks
    // This is used to log the event when the mic is muted
    useEffect(() => {
        if (onLogEventReady) {
            onLogEventReady(studentPreCheck.logEvent);
        }
    }, [studentPreCheck]);

    useEffect(() => {
        // Set the current precheck step in the recorder for Sentry logging
        if (typeof speechProps?.setRecorderPreCheckStep === 'function' && studentPreChecks.currentCheck) {
            speechProps.setRecorderPreCheckStep(studentPreChecks.currentCheck);
        }
        return () => {
            if (typeof speechProps?.setRecorderPreCheckStep === 'function') {
                speechProps.setRecorderPreCheckStep(null);
            }
        };
    }, [studentPreChecks.currentCheck, speechProps?.setRecorderPreCheckStep]);

    useEffect(() => {
        const shouldGoThroughFTUE = !userData.hasSeenFTUE && !isNonVerbalAssignment
        const shouldSkipFTUEIntro = !!localStorage.getItem(LOCAL_STORAGE_FLAGS.READY_MODULE_SKIP_FTUE_INTRO) // this is set true when a manual mic permission fix is done.
        const shouldDoAudioChecks = !!forcedAssessment && !isNonVerbalAssignment // audio checks are Volume and ASR checks, but skip for nonVerbal assignments
        const isFullyEnabled = userData.enablePretest // feature gates the rest of the module
        // TT-935: intro video eligibility. Whether a tutor session actually plays it is
        // decided inside IntroVideo, which skips it on devices the student has used before.
        const shouldShowIntroVideo = FEATURE_FLAGS.ENABLE_READY_MODULE_INTRO_VIDEO && !isNonVerbalAssignment && !teacherMicTest
        const { sessionStartTime, userId, sessionId, districtId, schoolId, grade, browser } = userData
        const settings = {
            studentId: userId,
            isFullyEnabled,
            shouldGoThroughFTUE: isFullyEnabled && shouldGoThroughFTUE,
            shouldDoAudioChecks: isFullyEnabled && shouldDoAudioChecks,
            shouldShowIntroVideo: isFullyEnabled && shouldShowIntroVideo,
            // TT-935: a test mode means someone is deliberately exercising the module, so
            // the intro video should play rather than being skipped as a familiar device.
            readyModuleTestMode: readyModuleTestMode || null,
            shouldSkipFTUEIntro,
            forcedAssessment: !!forcedAssessment,
            isNonVerbalAssignment: !!isNonVerbalAssignment,
            teacherMicTest: !!teacherMicTest,
            fromStudentInbox: !!fromStudentInbox,
            hasSeenFTUE: userData.hasSeenFTUE,
            sessionData: {
                userId,
                sessionId,
                sessionStartTime,
                districtId,
                schoolId,
                grade,
                browser,
            },
        }

        // readyModuleTestMode (e.g. SKIP from Restart Activity) only applies when not in mid-session troubleshoot.
        // When midSessionMicTroubleshoot is true, the user must go through ASR checks to fix audio issues.
        if(readyModuleTestMode && !midSessionMicTroubleshoot){
            if(shouldSkipReadyModule(readyModuleTestMode)){
                // End pre-checks immediately and return. Without the early return,
                // startStudentPreChecks also dispatches (because studentPreChecksStarted
                // is false), leaving stale currentCheck in Redux that renders the wrong
                // child component on subsequent mid-session troubleshoot remounts.
                dispatch(endStudentPreChecks())
                return
            }else{
                window.history.replaceState(null, null, `?readyModuleTestMode=${readyModuleTestMode}`);
                settings.shouldGoThroughFTUE = readyModuleTestMode === READY_MODULE_TEST_MODES.FTUE && !isNonVerbalAssignment
                settings.shouldDoAudioChecks = (settings.shouldGoThroughFTUE || readyModuleTestMode === READY_MODULE_TEST_MODES.RETURNED_USER_WITH_ASSESSMENTS) && !isNonVerbalAssignment
                settings.asrOnlyMode = readyModuleTestMode === READY_MODULE_TEST_MODES.ASR_ONLY
                console.log("Ready module settings test mode", settings)
            }
        }

        // If mid-session mic troubleshoot, skip directly to ASR checks
        if(midSessionMicTroubleshoot){
            settings.asrOnlyMode = true;
        }

        console.log("Ready module settings", settings)

        // In mid-session troubleshoot mode, reset Redux state first to clear stale values
        // (like studentPreChecksEnded: true from the previous session)
        if(midSessionMicTroubleshoot){
            dispatch(resetStudentPreChecks())
        }

        // Start or update the pre-checks flow
        // Use || midSessionMicTroubleshoot because after reset, studentPreChecksStarted will be false in Redux
        // but the closure still has the stale value
        if(!studentPreChecks.studentPreChecksStarted || midSessionMicTroubleshoot){
            // restore to normal path for next user
            localStorage.removeItem(LOCAL_STORAGE_FLAGS.READY_MODULE_SKIP_FTUE_INTRO)
            dispatch(startStudentPreChecks({settings}))
        }else{
            dispatch(updateSettings({settings}))
        }

    }, [readyModuleTestMode])

    useEffect(()=>{
        // Skip redirect when in mid-session troubleshoot mode — the navigation to
        // pre-checks was intentional and initialUserInteraction may be stale after
        // restartSession reloaded the page.
        if(!initialUserInteraction && !midSessionMicTroubleshoot) history.push('/student')
    }, [])

    // Set isMounted flag on mount/unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Cleanup effect to reset teacher mic test mode flag when component unmounts
    useEffect(() => {
        // Only reset the flag when the component unmounts, not on every render
        return () => {
            if (resetTeacherMicTestMode) {
                resetTeacherMicTestMode();
            }
        };
    }, []); // Empty dependency array means this only runs on mount/unmount

    useEffect(()=>{
        // In mid-session troubleshoot mode, don't auto-complete - user must go through ASR checks
        // The state from previous pre-checks may still show studentPreChecksEnded:true
        if(midSessionMicTroubleshoot) return;

        if(shouldSkipReadyModule(readyModuleTestMode)){
            onPreCheckDone()
        }else if(studentPreChecks.studentPreChecksEnded){
            // Check if this is a teacher mic test
            if (teacherMicTest && fromStudentInbox && returnUrl) {
                // Redirect to the return URL for teacher mic test
                window.location.href = returnUrl;
                return;
            }
            
            // if the user was going through FTUE and all checks ended, mark them as so.
            if(studentPreChecks.settings.shouldGoThroughFTUE){
                console.log("Mark Seen FTUE", userData.userId)
                StudentInformationService.setFTUESeen(userData.userId, true)
            }
            // tell App state that it is done.
            // doing this to avoid introducing redux to App component for now.
            onPreCheckDone()
        }
    }, [studentPreChecks.studentPreChecksEnded, teacherMicTest, returnUrl, fromStudentInbox, midSessionMicTroubleshoot])

    const isWaitingOnAsyncChecksToFinish = isWaitingOnAsyncChecks(studentPreChecks)

    const continueToNextCheckWithError = (error) => {
        dispatch(preCheckItemErroredAndMoveOn({item: studentPreChecks.currentCheck, error: error}))
    }

    /**
     * rendering
     */

    if(studentPreChecks.studentPreCheckFatalExit){
        return <FatalFailureScreen/>
    }

    // Guard: In mid-session troubleshoot mode, studentPreChecksEnded may be stale (true from previous session)
    // Wait for the useEffect to reset Redux state before deciding to redirect
    if(studentPreChecks.studentPreChecksEnded && !studentPreChecks.studentPreCheckFatalExit && !midSessionMicTroubleshoot){
        if (!isMountedRef.current) {
            return null;
        }
        return <Redirect to="/student"/>
    }

    if(!speechProps || !speechProps.speak){
        return <div className='invisible'>Loading Speech...</div>
    }

    if(!userData){
        return <div className='invisible'>Loading user data...</div>
    }

    const showMicIcon = () => {
        setMicIcon(true);
    }

    const hideMicIcon = () => {
        setMicIcon(false);
    }

    return <div className='student-pre-checks'>
        {isWaitingOnAsyncChecksToFinish && <div>Waiting for some background checks to finish.</div>}
        <StudentPreCheckErrorBoundary logEvent={studentPreCheck.logEvent} continueToNextCheckWithError={continueToNextCheckWithError}>
            <VirtualScreen listeningStatusEnabled={micIcon} invisible={props.screenInvisible}>
                <CheckWrapper>
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.introVideo && <IntroVideo speechProps={speechProps}/>}
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.networkSpeedChecks && <NetworkSpeedCheck speechProps={speechProps} toggleReaderContainer={props.toggleReaderContainer}/>}
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.networkPingChecks && <NetworkPingChecks speechProps={speechProps} toggleReaderContainer={props.toggleReaderContainer}/>}
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.hardwareChecks && <HardwareChecks speechProps={speechProps} toggleReaderContainer={props.toggleReaderContainer} failureReturnUrl={failureReturnUrl}/>}
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks && <StudentVolumeChecks speechProps={speechProps} showMicIcon={showMicIcon} toggleReaderContainer={props.toggleReaderContainer}/>}
                    {studentPreChecks.currentCheck === PRE_CHECK_MACHINE_STATES.studentASRChecks && <StudentASRChecks history={history} speechProps={speechProps} showMicIcon={showMicIcon} hideMicIcon={hideMicIcon} toggleReaderContainer={props.toggleReaderContainer} readerContainerVisible={!props.screenInvisible} readerManagerRef={readerManagerRef} midSessionMicTroubleshoot={midSessionMicTroubleshoot} audioIssueType={audioIssueType} midSessionTroubleshootSeen={midSessionTroubleshootSeen} onRestartSession={onRestartSession} onRestartSessionSuppressingTroubleshoot={onRestartSessionSuppressingTroubleshoot}/>}
                </CheckWrapper>
            </VirtualScreen>
        </StudentPreCheckErrorBoundary>
    </div>
}
export default React.memo(StudentPreChecks)
